import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Warning as WarningIcon,
  AccountBalanceWallet as WalletIcon,
  Gavel as DisputeIcon,
  Inventory as InventoryIcon,
  VerifiedUser as VerifiedIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { AttentionLists as AttentionData } from '../../services/dashboardService';

const faNum = (n: number) => (n ?? 0).toLocaleString('fa-IR');
const faToman = (n: number) => `${(n ?? 0).toLocaleString('fa-IR')} تومان`;
const faDate = (iso?: string) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fa-IR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return '—';
  }
};

const DISPUTE_REASON: Record<string, string> = {
  wrong_account: 'کالای اشتباه',
  account_not_working: 'کالا معیوب است',
  missing_items: 'اقلام ناقص',
  other: 'سایر',
};

interface Props {
  data: AttentionData | null;
  loading: boolean;
}

const ListCard: React.FC<{
  title: string;
  icon: React.ReactNode;
  count: number;
  moreHref: string;
  children: React.ReactNode;
  empty?: boolean;
}> = ({ title, icon, count, moreHref, children, empty }) => {
  const navigate = useNavigate();
  return (
    <div className="admin-card">
      <div className="admin-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {icon}
          <h2 style={{ fontSize: '1rem' }}>{title}</h2>
          {count > 0 && <span className="dash-badge">{faNum(count)}</span>}
        </div>
        <button className="dash-range-btn" onClick={() => navigate(moreHref)} title="مشاهده همه">
          همه <OpenIcon style={{ width: 14, height: 14, verticalAlign: 'middle' }} />
        </button>
      </div>
      <div className="admin-card-body" style={{ padding: 0 }}>
        {empty ? (
          <div className="empty-state" style={{ padding: '24px 0' }}>
            <p>موردی وجود ندارد ✅</p>
          </div>
        ) : (
          <table className="dash-table">
            <tbody>{children}</tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const AttentionLists: React.FC<Props> = ({ data, loading }) => {
  const navigate = useNavigate();

  if (loading && !data) {
    return (
      <div className="content-grid">
        {[0, 1].map((i) => (
          <div key={i} className="admin-card">
            <div className="admin-card-body">
              {[0, 1, 2].map((j) => (
                <div key={j} className="skeleton-line w-75" style={{ marginBottom: 12 }} />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  const d = data;

  return (
    <>
      <div className="content-grid">
        <ListCard
          title="سفارش‌های مشکل‌دار"
          icon={<WarningIcon style={{ width: 20, height: 20, color: '#f97316' }} />}
          count={d?.problematicOrders.length ?? 0}
          moreHref="/orders"
          empty={!d?.problematicOrders.length}
        >
          {d?.problematicOrders.map((o) => (
            <tr key={o.id} className="dash-clickable" onClick={() => navigate('/orders')}>
              <td className="mono">#{o.orderNumber}</td>
              <td>{o.customer ?? '—'}</td>
              <td>{faToman(o.totalAmount)}</td>
              <td className="muted">{faDate(o.createdAt)}</td>
            </tr>
          ))}
        </ListCard>

        <ListCard
          title="برداشت‌های در انتظار"
          icon={<WalletIcon style={{ width: 20, height: 20, color: '#f59e0b' }} />}
          count={d?.pendingWithdrawals.length ?? 0}
          moreHref="/user-withdrawal-requests"
          empty={!d?.pendingWithdrawals.length}
        >
          {d?.pendingWithdrawals.map((w) => (
            <tr key={w.id} className="dash-clickable" onClick={() => navigate('/user-withdrawal-requests')}>
              <td>{w.customer ?? '—'}</td>
              <td>{faToman(w.amount)}</td>
              <td className="muted">{faDate(w.requestDate)}</td>
            </tr>
          ))}
        </ListCard>
      </div>

      <div className="content-grid">
        <ListCard
          title="اختلافات باز"
          icon={<DisputeIcon style={{ width: 20, height: 20, color: '#ef4444' }} />}
          count={d?.openDisputes.length ?? 0}
          moreHref="/dispute-center"
          empty={!d?.openDisputes.length}
        >
          {d?.openDisputes.map((o) => (
            <tr key={o.id} className="dash-clickable" onClick={() => navigate('/dispute-center')}>
              <td className="mono">#{o.orderNumber}</td>
              <td>{o.customer ?? '—'}</td>
              <td>{o.reason ? DISPUTE_REASON[o.reason] ?? o.reason : '—'}</td>
              <td className="muted">{faDate(o.createdAt)}</td>
            </tr>
          ))}
        </ListCard>

        <ListCard
          title="محصولات کم‌موجودی"
          icon={<InventoryIcon style={{ width: 20, height: 20, color: '#0ea5e9' }} />}
          count={d?.lowStockProducts.length ?? 0}
          moreHref="/products"
          empty={!d?.lowStockProducts.length}
        >
          {d?.lowStockProducts.map((p) => (
            <tr key={p.id} className="dash-clickable" onClick={() => navigate('/products')}>
              <td>{p.name}</td>
              <td>{p.store ?? '—'}</td>
              <td style={{ color: '#ef4444', fontWeight: 600 }}>{faNum(p.stock)} عدد</td>
            </tr>
          ))}
        </ListCard>
      </div>

      <ListCard
        title="محصولات در انتظار تایید"
        icon={<VerifiedIcon style={{ width: 20, height: 20, color: '#a855f7' }} />}
        count={d?.pendingProducts.length ?? 0}
        moreHref="/product-approvals"
        empty={!d?.pendingProducts.length}
      >
        {d?.pendingProducts.map((p) => (
          <tr key={p.id} className="dash-clickable" onClick={() => navigate('/product-approvals')}>
            <td>{p.name}</td>
            <td>{p.store ?? '—'}</td>
            <td className="muted">{faDate(p.createdAt)}</td>
          </tr>
        ))}
      </ListCard>
    </>
  );
};

export default AttentionLists;
